import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DEFAULT_THEME_COLORS, type ThemeColors } from '@/domain/models/theme.js';
import { ErrorState, LoadingState } from '@/presentation/components/index.js';
import { areThemeColorsValid, ThemePaletteEditor } from '@/presentation/components/theme-palette-editor/theme-palette-editor.js';
import type { ThemeController } from '@/presentation/controllers/theme-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { primaryCtaGradient } from '@/shared/utils/brand-gradient.js';

interface Props {
    controller: ThemeController;
}

const PRIMARY_BUTTON_SX = {
    textTransform: 'none' as const,
    background: primaryCtaGradient,
    color: 'white',
    '&:hover': { opacity: 0.9, background: primaryCtaGradient }
};

export function AppearancePage({ controller }: Props) {
    const { translate, useThemeStore } = useApp();
    const setDefaultColors = useThemeStore((s) => s.setDefaultColors);
    const [colors, setColors] = useState<ThemeColors>({ ...DEFAULT_THEME_COLORS });
    const [serverColors, setServerColors] = useState<ThemeColors>({ ...DEFAULT_THEME_COLORS });
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState('');
    const serverColorsRef = useRef(serverColors);

    useEffect(() => {
        serverColorsRef.current = serverColors;
    }, [serverColors]);

    const loadDefaultTheme = useCallback(async () => {
        setIsLoading(true);
        setLoadFailed(false);
        setError('');
        const result = await controller.getDefaultTheme();
        result.fold(
            () => {
                setLoadFailed(true);
                setIsLoading(false);
            },
            (data) => {
                const next = data.colors ? { ...data.colors } : { ...DEFAULT_THEME_COLORS };
                setColors(next);
                setServerColors(next);
                setDefaultColors(next);
                setIsLoading(false);
            }
        );
    }, [controller, setDefaultColors]);

    useEffect(() => {
        loadDefaultTheme();
    }, [loadDefaultTheme]);

    useEffect(() => {
        return () => {
            setDefaultColors({ ...serverColorsRef.current });
        };
    }, [setDefaultColors]);

    const handleColorsChange = (next: ThemeColors) => {
        setColors(next);
        if (areThemeColorsValid(next)) {
            setDefaultColors(next);
        }
    };

    const handleDiscard = () => {
        const restored = { ...serverColors };
        setColors(restored);
        setDefaultColors(restored);
        setError('');
    };

    const handleRestoreFactory = () => {
        const factory = { ...DEFAULT_THEME_COLORS };
        setColors(factory);
        setDefaultColors(factory);
        setError('');
    };

    const handleSave = async () => {
        if (!areThemeColorsValid(colors)) {
            setError(translate('appearance.invalidColors'));
            return;
        }
        setIsSaving(true);
        setError('');
        const result = await controller.updateDefaultTheme(colors);
        result.fold(
            () => {
                setError(translate('appearance.errorSaving'));
                setIsSaving(false);
            },
            (data) => {
                const saved = { ...data.colors };
                setColors(saved);
                setServerColors(saved);
                setDefaultColors(saved);
                setToast(translate('appearance.saved'));
                setTimeout(() => setToast(''), 4000);
                setIsSaving(false);
            }
        );
    };

    if (isLoading) {
        return (
            <Box sx={{ p: 3 }}>
                <LoadingState />
            </Box>
        );
    }

    if (loadFailed) {
        return (
            <Box sx={{ p: 3 }}>
                <ErrorState message={translate('appearance.errorLoading')} onRetry={loadDefaultTheme} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 960 }}>
            {toast && (
                <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, bgcolor: 'success.main', color: 'white', px: 2, py: 1.5, borderRadius: 2, boxShadow: 3 }}>
                    {toast}
                </Box>
            )}
            <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                {translate('appearance.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('appearance.description')}
            </Typography>
            {error && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1, mb: 3 }}>
                <CardContent sx={{ p: 3, '&:last-of-type': { pb: 3 } }}>
                    <ThemePaletteEditor colors={colors} onColorsChange={handleColorsChange} />
                </CardContent>
            </Card>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleRestoreFactory} sx={{ textTransform: 'none' }}>
                    {translate('appearance.restoreFactory')}
                </Button>
                <Button variant="outlined" onClick={handleDiscard} sx={{ textTransform: 'none' }}>
                    {translate('appearance.discard')}
                </Button>
                <Button variant="contained" disableElevation disabled={isSaving || !areThemeColorsValid(colors)} onClick={handleSave} sx={PRIMARY_BUTTON_SX}>
                    {isSaving ? translate('common.pleaseWait') : translate('common.save')}
                </Button>
            </Stack>
        </Box>
    );
}
